import { BUILD } from '../../config';
import { terrainHeight } from '../../engine/terrain';

// Square AND triangle footprint math, kept deliberately SEPARATE: triangles are NOT half-squares.
// Squares have 4 edges at 90deg; equilateral triangles have 3 edges at 120deg, with their own
// corner radius (TRI_R = S/sqrt(3)) and edge-midpoint construction. This module is the verbatim
// geometry core that the foundation/ceiling solver paths delegate to (corners, edges, footprint,
// SAT overlap, edge-mate offset, point-in-footprint, terrain sampling). It is standalone — it does
// NOT import from building.ts; building.ts imports these later.

export const S = BUILD.size; // foundation edge length
export const TRI_R = S / Math.sqrt(3); // equilateral-triangle circumradius
export const FOOTPRINT_SHRINK = 0.96; // shrink so flush edge-to-edge placements only touch, not overlap

export type FootprintShape = 'square' | 'triangle';
export type FootprintFamily = 'square' | 'triangle' | 'none';

// A perimeter edge: midpoint (mx,mz) in the piece's local frame plus its outward unit normal (nx,nz).
export interface EdgeDef { mx: number; mz: number; nx: number; nz: number; }

export const SQUARE_EDGES: EdgeDef[] = [
  { mx: S / 2, mz: 0, nx: 1, nz: 0 },
  { mx: -S / 2, mz: 0, nx: -1, nz: 0 },
  { mx: 0, mz: S / 2, nx: 0, nz: 1 },
  { mx: 0, mz: -S / 2, nx: 0, nz: -1 },
];

export const SQUARE_CORNERS = [
  { x: S / 2, z: S / 2 },
  { x: S / 2, z: -S / 2 },
  { x: -S / 2, z: -S / 2 },
  { x: -S / 2, z: S / 2 },
];

export const TRI_CORNERS = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3].map((t) => ({
  x: TRI_R * Math.sin(t),
  z: TRI_R * Math.cos(t),
}));

export function buildTriEdges(): EdgeDef[] {
  const v = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3].map((t) => ({ x: TRI_R * Math.sin(t), z: TRI_R * Math.cos(t) }));
  const pairs: Array<[number, number]> = [[1, 2], [0, 1], [2, 0]];
  return pairs.map(([a, b]) => {
    const mx = (v[a].x + v[b].x) / 2;
    const mz = (v[a].z + v[b].z) / 2;
    const len = Math.hypot(mx, mz);
    return { mx, mz, nx: mx / len, nz: mz / len };
  });
}

export const TRI_EDGES: EdgeDef[] = buildTriEdges();

export function edgesFor(type: FootprintShape): EdgeDef[] {
  return type === 'square' ? SQUARE_EDGES : TRI_EDGES;
}

// Rotate (x,z) by yaw in the same convention the placed meshes use (Three's group.rotation.y).
export function rotXZ(x: number, z: number, yaw: number): { x: number; z: number } {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  return { x: c * x + s * z, z: -s * x + c * z };
}

export function angle(x: number, z: number): number {
  return Math.atan2(x, z);
}

export function mod(a: number, n: number): number {
  return ((a % n) + n) % n;
}

export function snapDeg(deg: number): number {
  return Math.round(deg / BUILD.rotateStepDeg) * BUILD.rotateStepDeg;
}

export function keyOf(x: number, z: number): string {
  return `${Math.round(x * 4)},${Math.round(z * 4)}`;
}

// Wall socket key includes the storey height, so a wall on a foundation and a wall on the ceiling
// above it (same x,z) stack independently instead of sharing one counter.
export function wallKey(x: number, z: number, supportTop: number): string {
  return `${keyOf(x, z)}@${Math.round(supportTop * 2)}`;
}

// Edge-mate offset: where the new piece's center lands so its matching edge sits flush against an
// existing edge at world (sx,sz) whose outward normal is (nx,nz). The yaw is derived so the new
// piece's reference edge points back along -n. `shape` selects which edge table to mate against
// (ceiling_square must use SQUARE_EDGES even though it is a ceiling, etc.).
export function mate(sx: number, sz: number, nx: number, nz: number, shape: FootprintShape): { x: number; z: number; yaw: number } {
  const ref = (shape === 'square' ? SQUARE_EDGES : TRI_EDGES)[0];
  const yaw = angle(-nx, -nz) - angle(ref.nx, ref.nz);
  const rm = rotXZ(ref.mx, ref.mz, yaw);
  return { x: sx - rm.x, z: sz - rm.z, yaw };
}

// Footprint polygon (shrunk a touch) of a placed piece, in world XZ.
export function footprint(type: FootprintShape, x: number, z: number, yaw: number): Array<{ x: number; z: number }> {
  const local = type === 'square' ? SQUARE_CORNERS : TRI_CORNERS;
  return local.map((c) => {
    const r = rotXZ(c.x * FOOTPRINT_SHRINK, c.z * FOOTPRINT_SHRINK, yaw);
    return { x: x + r.x, z: z + r.z };
  });
}

// Lowest and highest terrain under a footprint's corners (drives the fixed-height fit + validity).
export function footprintTerrain(type: FootprintShape, x: number, z: number, yaw: number): { minC: number; maxC: number } {
  let minC = Infinity, maxC = -Infinity;
  for (const c of footprint(type, x, z, yaw)) {
    const y = terrainHeight(c.x, c.z);
    if (y < minC) minC = y;
    if (y > maxC) maxC = y;
  }
  return { minC, maxC };
}

// Point-in-footprint test in a placed piece's local frame (square or equilateral triangle).
export function pointInFootprint(type: FootprintShape, px: number, pz: number, fx: number, fz: number, yaw: number): boolean {
  const dx = px - fx, dz = pz - fz;
  const c = Math.cos(yaw), s = Math.sin(yaw);
  const lx = c * dx - s * dz;
  const lz = s * dx + c * dz;
  if (type === 'square') return Math.abs(lx) <= S / 2 && Math.abs(lz) <= S / 2;
  const R = TRI_R;
  const v0x = 0, v0z = R, v1x = 0.8660254 * R, v1z = -0.5 * R, v2x = -0.8660254 * R, v2z = -0.5 * R;
  const sign = (ax: number, az: number, bx: number, bz: number, cx: number, cz: number): number =>
    (ax - cx) * (bz - cz) - (bx - cx) * (az - cz);
  const d1 = sign(lx, lz, v0x, v0z, v1x, v1z);
  const d2 = sign(lx, lz, v1x, v1z, v2x, v2z);
  const d3 = sign(lx, lz, v2x, v2z, v0x, v0z);
  return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
}

// --- footprint overlap (SAT for two convex polygons in the XZ plane) ---

export function polyOverlap(a: Array<{ x: number; z: number }>, b: Array<{ x: number; z: number }>): boolean {
  return !hasSeparatingAxis(a, b) && !hasSeparatingAxis(b, a);
}

function hasSeparatingAxis(poly: Array<{ x: number; z: number }>, other: Array<{ x: number; z: number }>): boolean {
  for (let i = 0; i < poly.length; i++) {
    const p1 = poly[i];
    const p2 = poly[(i + 1) % poly.length];
    const ax = -(p2.z - p1.z);
    const az = p2.x - p1.x;
    let minA = Infinity, maxA = -Infinity, minB = Infinity, maxB = -Infinity;
    for (const p of poly) { const d = p.x * ax + p.z * az; if (d < minA) minA = d; if (d > maxA) maxA = d; }
    for (const p of other) { const d = p.x * ax + p.z * az; if (d < minB) minB = d; if (d > maxB) maxB = d; }
    if (maxA < minB || maxB < minA) return true;
  }
  return false;
}

